import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import ToggleListItem from '@components/ToggleListItem'

import { toggleNestedValue } from '@functions/entity'

import usePublication, { usePublicationForm } from '@hooks/usePublication'

const BOOKING_CLASSIFICATION_PRESETS = ['Real Estate Guide 6', 'Commercial Property 6', 'Development 6']

const EditPublicationDeliverySettingsModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, updatePublication } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { publication } = modalPayload

  const {
    callbacks: { updatePublication: updateFn },
    updating,
  } = usePublication(publication)

  const publicationForm = usePublicationForm(publication)

  const {
    entityState,
    entityState: {
      artwork_email,
      booking_api,
      booking_email,
      editorial_email,
      instructions,
      ofi_email,
      options: {
        accept_bookings_after_deadline,
        allow_user_revert,
        booking_api_mode,
        booking_classification,
        booking_confirmation_id_method,
        cancellation_api_enabled,
        disable_api_bookings_after_deadline,
        inspection_format,
      },
    },
    handlers,
    setEntityState,
  } = publicationForm

  const [isCustomBookingClassification, setIsCustomBookingClassification] = useState(() => {
    const initialBookingClassification = digObject(publication, 'options.booking_classification', '')

    return !!initialBookingClassification && !BOOKING_CLASSIFICATION_PRESETS.includes(initialBookingClassification)
  })

  const bookingClassificationPreset = isCustomBookingClassification
    ? 'custom'
    : (booking_classification || 'Real Estate Guide 6')

  const handleBookingClassificationPresetChange = (event) => {
    const { value } = event.target

    setIsCustomBookingClassification(value === 'custom')

    setEntityState({
      options: {
        ...entityState.options,
        booking_classification: value === 'custom' ? '' : value,
      },
    })
  }

  const bookingClassificationMissing = isCustomBookingClassification && !booking_classification

  const resolvedBookingClassification = isCustomBookingClassification
    ? booking_classification
    : bookingClassificationPreset

  const updatePublicationPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: {
      ...entityState,
      ...(booking_api === 'newscorp-xml' && {
        options: {
          ...entityState.options,
          booking_classification: resolvedBookingClassification,
        },
      }),
    },
    toastText: 'Delivery Settings Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Delivery" titleSecondLine="Settings" />

      <SidebarModal.Content>
        <Form.Field label="Booking Confirmation ID">
          <select
            name="options.booking_confirmation_id_method"
            value={booking_confirmation_id_method}
            {...handlers}
          >
            <option value="">None</option>
            <option value="api">API Integration</option>
            <option value="manual">Manually Entered</option>
            <option value="system">System Generated</option>
          </select>
        </Form.Field>

        <Form.Field label="Booking API" marginTop="large">
          <select name="booking_api" value={booking_api} {...handlers}>
            <option value="">None</option>
            <option value="domain-xml">Domain XML</option>
            <option value="newscorp-xml">Newscorp XML</option>
          </select>
        </Form.Field>

        <SectionDivider boxProps={{ marginY: 'large' }}>DESTINATION</SectionDivider>

        <Form.Field label="Send Bookings To">
          <input
            name="booking_email"
            type="email"
            value={booking_email}
            {...handlers}
          />
        </Form.Field>

        <Form.Field label="Send Artworks To" marginTop="large">
          <input
            name="artwork_email"
            type="email"
            value={artwork_email}
            {...handlers}
          />
        </Form.Field>

        <Form.Field label="Send Editorials To" marginTop="large">
          <input
            name="editorial_email"
            type="email"
            value={editorial_email}
            {...handlers}
          />
        </Form.Field>

        <Form.Field label="Send OFI's To" marginTop="large">
          <input
            name="ofi_email"
            type="email"
            value={ofi_email}
            {...handlers}
          />
        </Form.Field>

        <SectionDivider boxProps={{ marginY: 'large' }}>INSPECTIONS</SectionDivider>

        <Form.Field label="Inspection Format">
          <select name="options.inspection_format" value={inspection_format} {...handlers}>
            <option value="newscorp_combined">Newscorp Combined (Default)</option>
            <option value="newscorp_separated">Newscorp Separated</option>
            <option value="newscorp_quest">Newscorp Quest</option>
            <option value="newscorp_courier_mail">Newscorp Courier Mail</option>
            <option value="newscorp_leader">Newscorp Leader</option>
            <option value="fairfax_busselton_mail">Fairfax Busselton Mail</option>
          </select>
        </Form.Field>

        <Form.Field label="Instructions" marginTop="large">
          <textarea
            name="instructions"
            style={{ height: 120 }}
            type="text"
            value={instructions}
            {...handlers}
          />
        </Form.Field>

        {booking_api !== 'domain-xml' && (
          <SectionDivider boxProps={{ marginY: 'large' }}>SETTINGS</SectionDivider>
        )}

        {!booking_api && (
          <ToggleListItem
            onClick={() => toggleNestedValue(publicationForm, 'options.allow_user_revert', { shouldSetState: true })}
            toggleValue={allow_user_revert}
          >
            <ToggleListItem.Title>User can revert bookings</ToggleListItem.Title>

            <ToggleListItem.Description>
              The customer will be able to revert their own bookings for this publication
            </ToggleListItem.Description>
          </ToggleListItem>
        )}

        {booking_api === 'newscorp-xml' && (
          <>
            <Form.Field label="API Mode">
              <select
                name="options.booking_api_mode"
                value={booking_api_mode}
                {...handlers}
              >
                <option value="">None</option>
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </Form.Field>

            <Form.Field label="Booking Classification" marginTop="large">
              <select
                onChange={handleBookingClassificationPresetChange}
                value={bookingClassificationPreset}
              >
                {BOOKING_CLASSIFICATION_PRESETS.map(preset => (
                  <option key={preset} value={preset}>{preset}</option>
                ))}
                <option value="custom">Custom…</option>
              </select>

              {isCustomBookingClassification && (
                <input
                  name="options.booking_classification"
                  style={{ marginTop: 8 }}
                  type="text"
                  value={booking_classification}
                  {...handlers}
                />
              )}

              {bookingClassificationMissing && (
                <Text color="red" fontSize="small" marginTop="small">
                  Enter a custom classification, or choose a preset.
                </Text>
              )}
            </Form.Field>

            <ToggleListItem
              boxProps={{ marginTop: 'large' }}
              onClick={() => toggleNestedValue(
                publicationForm,
                'options.cancellation_api_enabled',
                { shouldSetState: true },
              )}
              toggleOffValue="Disabled"
              toggleOnValue="Enabled"
              toggleValue={cancellation_api_enabled}
            >
              <ToggleListItem.Title>Cancellation API</ToggleListItem.Title>

              <ToggleListItem.Description>
                Bookings can be cancelled/reverted via API by the provider
              </ToggleListItem.Description>
            </ToggleListItem>
          </>
        )}

        <ToggleListItem
          onClick={() => toggleNestedValue(
            publicationForm,
            'options.accept_bookings_after_deadline',
            { shouldSetState: true },
          )}
          toggleValue={accept_bookings_after_deadline}
        >
          <ToggleListItem.Title>Accept bookings after due date</ToggleListItem.Title>

          <ToggleListItem.Description>
            Publication accepts bookings after the due date and time.
          </ToggleListItem.Description>
        </ToggleListItem>

        <ToggleListItem
          onClick={() => toggleNestedValue(
            publicationForm,
            'options.disable_api_bookings_after_deadline',
            { shouldSetState: true },
          )}
          toggleValue={!disable_api_bookings_after_deadline}
        >
          <ToggleListItem.Title>API Bookings Feed</ToggleListItem.Title>

          <ToggleListItem.Description>
            API Bookings feed after deadline has passed
          </ToggleListItem.Description>
        </ToggleListItem>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={bookingClassificationMissing}
          loading={updating}
          onClick={() => updatePublication(updatePublicationPayload)}
          size="large"
        >
          Save Delivery Settings
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

EditPublicationDeliverySettingsModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

EditPublicationDeliverySettingsModal.defaultProps = {
  modalKey: 'EditPublicationDeliverySettingsModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <EditPublicationDeliverySettingsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
